import type { Theme, SxProps, Breakpoint } from '@mui/material/styles';

import { useState } from 'react';

import Box from '@mui/material/Box';
import { useTheme } from '@mui/material/styles';

import { _langs } from '_mock';

import { Main } from './main';
import { layoutClasses } from '../classes';
import { NavMobile, NavDesktop } from './nav';
import { navData } from '../config-nav-dashboard';
import { Searchbar } from '../components/searchbar';
import { MenuButton } from '../components/menu-button';
import { LayoutSection } from '../core/layout-section';
import { HeaderSection } from '../core/header-section';
import { AccountPopover } from '../components/account-popover';
import { SettingsPopover } from '../components/settings-popover';
import { LanguagePopover } from '../components/language-popover';
import { NotificationsPopover } from '../components/notifications-popover';
import ChatProvider from 'components/Chat/ChatProvider';
import { ChatButton } from 'components/Chat/ChatButton';
import { ChatDrawer } from 'components/Chat/ChatDrawer';
import intl from 'components/i18n/ReactIntlWrapper';
import { ENTITY_TYPE } from 'common/constants';
import { AccountTree, AccountBalance, Badge, Business, Extension, GridView, ManageAccounts, Notifications, PersonAddAlt, Receipt, Security, Translate, VpnKey, SmartToyOutlined } from '@mui/icons-material';
import Footer from './footer';
import ErrorBoundary from 'components/ErrorBoundary/ErrorBoundary';
import { Backdrop } from '@mui/material';
import Spinner from 'components/Spinner';
import { useLayout } from 'components/Layout/LayoutProvider';
import { useEffect } from 'react';


// ----------------------------------------------------------------------

export type DashboardLayoutProps = {
  sx?: SxProps<Theme>;
  children: React.ReactNode;
  header?: {
    sx?: SxProps<Theme>;
  };
};

export function DashboardLayout({ sx, children, header }: DashboardLayoutProps) {
  const theme = useTheme();

  const [navOpen, setNavOpen] = useState(false);

  const layoutQuery: Breakpoint = 'lg';

  const { loading, setLayoutLoadingMode } = useLayout();

  useEffect(() => {
    setLayoutLoadingMode('layout');
    return () => {
      setLayoutLoadingMode('full-screen');
    };
  }, [setLayoutLoadingMode]);
  
  return (
    <ChatProvider>
    <LayoutSection
      /** **************************************
       * Header
       *************************************** */
      headerSection={
        <HeaderSection
          layoutQuery={layoutQuery}
          slotProps={{
            container: {
              maxWidth: false,
              sx: { px: { [layoutQuery]: 2 } },
            },
          }}
          sx={header?.sx}
          slots={{
            leftArea: (
              <>
                <MenuButton
                  onClick={() => setNavOpen(true)}
                  sx={{
                    ml: -1,
                    [theme.breakpoints.up(layoutQuery)]: { display: 'none' },
                  }}
                />
                <NavMobile
                  data={navData}
                  open={navOpen}
                  onClose={() => setNavOpen(false)}
                />
              </>
            ),
            rightArea: (
              <Box gap={1} display="flex" alignItems="center">
                <LanguagePopover data={_langs} />
                <ChatButton />
                <NotificationsPopover/>
                <SettingsPopover
                  data={[
                    {
                      label: intl.get('mainNavigation.setting.users'),
                      href: '/users',
                      icon: <PersonAddAlt />,
                      viewRoles: [],
                      editRoles: [],
                      entityType: ENTITY_TYPE.EMPLOYEE,
                      identifier: 'ui.nav.settings.users',
                    },
                    {
                      label: intl.get('mainNavigation.setting.roles'),
                      href: '/roles',
                      icon: <Security />,
                      viewRoles: [],
                      editRoles: [],
                      entityType: ENTITY_TYPE.EMPLOYEE,
                      identifier: 'ui.nav.settings.users',
                    },
                    {
                      label: intl.get('mainNavigation.setting.security'),
                      href: '',
                      icon: <Security />,
                      entityType: ENTITY_TYPE.EMPLOYEE,
                      identifier: 'ui.nav.settings.admin',
                      children: [
                        {
                          label: intl.get('mainNavigation.setting.scopes'),
                          href: '/scopes',
                          icon: <VpnKey />,
                          entityType: ENTITY_TYPE.EMPLOYEE,
                          identifier: 'ui.admin.scopes',
                        },
                        {
                          label: intl.get('mainNavigation.setting.functionalities'),
                          href: '/functionalities',
                          icon: <Extension />,
                          entityType: ENTITY_TYPE.EMPLOYEE,
                          identifier: 'ui.admin.functionalities',
                        },
                        {
                          label: intl.get('mainNavigation.setting.functionalityMatrix'),
                          href: '/functionality/matrix',
                          icon: <GridView />,
                          entityType: ENTITY_TYPE.EMPLOYEE,
                          identifier: 'ui.admin.functionality-matrix',
                        },
                      ],
                    },
                    {
                      label: intl.get('mainNavigation.setting.workflows'),
                      href: '/workflows',
                      icon: <AccountTree />,
                      viewRoles: [],
                      editRoles: [],
                      entityType: ENTITY_TYPE.EMPLOYEE,
                      identifier: 'ui.nav.settings.workflows',
                    },
                    {
                      label: intl.get('mainNavigation.setting.corporateEntities'),
                      href: '/corporateEntities',
                      icon: <Business />,
                      viewRoles: [],
                      editRoles: [],
                      entityType: ENTITY_TYPE.EMPLOYEE,
                      identifier: 'ui.nav.settings.admin',
                    },
                    {
                      label: intl.get('notification.configuration.title'),
                      href: '/notifications/configuration',
                      icon: <Notifications />,
                      viewRoles: [],
                      editRoles: [],
                      entityType: ENTITY_TYPE.EMPLOYEE,
                      identifier: 'ui.nav.settings.notifications',
                    },
                    {
                      label: intl.get('invoiceConfig.title'),
                      href: '/invoice-configuration',
                      icon: <Receipt />,
                      viewRoles: [],
                      editRoles: [],
                      entityType: ENTITY_TYPE.EMPLOYEE,
                      identifier: 'ui.nav.settings.admin',
                      children: [
                        {
                          label: intl.get('mainNavigation.billing.taxes'),
                          href: '/taxes',
                          icon: <Receipt />,
                          viewRoles: [],
                          editRoles: [],
                          entityType: ENTITY_TYPE.EMPLOYEE,
                          identifier: 'ui.nav.settings.tax',
                        },
                        {
                          label: intl.get('invoiceTypeManagement.title') || "test",
                          href: '/invoice-types',
                          icon: <Receipt />,
                          viewRoles: [],
                          editRoles: [],
                          entityType: ENTITY_TYPE.EMPLOYEE,
                          identifier: 'ui.nav.settings.invoice-type',
                        },
                      ]
                    },
                    {
                      label: intl.get('mainNavigation.setting.translations'),
                      href: '/translations',
                      icon: <Translate />,
                      viewRoles: [],
                      editRoles: [],
                      entityType: ENTITY_TYPE.EMPLOYEE,
                      identifier: 'ui.nav.settings.locale',
                    },
                    {
                      label: intl.get('mainNavigation.billing.taxes'),
                      href: '/taxes',
                      icon: <Receipt />,
                      viewRoles: [],
                      editRoles: [],
                      entityType: ENTITY_TYPE.EMPLOYEE,
                      identifier: 'ui.nav.settings.tax',
                    },
                    {
                      label: intl.get('accountManagement.title'),
                      href: '/accounts',
                      icon: <AccountBalance />,
                      viewRoles: [],
                      editRoles: [],
                      entityType: ENTITY_TYPE.CORPORATE_ENTITY,
                      identifier: 'ui.nav.settings.accounts',
                    },
                    {
                      label: intl.get('mainNavigation.aiCredits'),
                      href: '/ai-credits',
                      viewRoles: [],
                                        editRoles: [],
                            entityType: ENTITY_TYPE.EMPLOYEE,           
                      icon: <SmartToyOutlined />,
                      identifier: 'ui.nav.ai-credits',
                    },

                    
                  ]}
                />
                <AccountPopover
                  data={[
                    {
                      label: intl.get('profile.pageTitle') || 'My Profile',
                      href: '/profile',
                      icon: <ManageAccounts />,
                    },
                  ]}
                />
              </Box>
            ),
          }}
        />
      }
      /** **************************************
       * Sidebar
       *************************************** */
      sidebarSection={
        <NavDesktop data={navData} layoutQuery={layoutQuery} />
      }
      /** **************************************
       * Footer
       *************************************** */
      footerSection={<Footer />}
      /** **************************************
       * Style
       *************************************** */
      cssVars={{
        '--layout-nav-vertical-width': '300px',
        '--layout-dashboard-content-pt': theme.spacing(1),
        '--layout-dashboard-content-pb': theme.spacing(5),
        '--layout-dashboard-content-px': theme.spacing(2),
      }}
      sx={{
        [`& .${layoutClasses.hasSidebar}`]: {
          [theme.breakpoints.up(layoutQuery)]: {
            pl: 'var(--layout-nav-vertical-width)',
          },
        },
        ...sx,
      }}
    >
      <ErrorBoundary>
        <Main>
          <Backdrop
            open={loading} // This should be controlled by a state variable if you want to show/hide the spinner
            sx={(theme) => ({
              zIndex: theme.zIndex.drawer + 99,
              position: 'absolute',
              width: '100%',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: 'rgba(220, 220, 220, 0.8)',
            })}
          >
            <Spinner small />
          </Backdrop>
          {children}
        </Main>
      </ErrorBoundary>
    </LayoutSection>
    <ChatDrawer />
    </ChatProvider>
  );
}
